import clone from 'lodash/clone';
import { getCanvasState, getLoadings, getTempState, getViewport } from '@shakkerai/pro-store';
import { initialTaskData, createOutpaintGenerator, createUpscaleGenerator } from '@shakkerai/pro-store';
import { fetchImageSegmentShapes, fetchImageStyle, tryTranslatePrompt } from '@shakkerai/pro-utils';
import { getGenerateSizeFromElement, updateGenerateParams } from '@shakkerai/pro-store/src/state/canvas';
import { createInpaintGenerator, createChangefaceGenerator } from '@shakkerai/pro-store/src/state/canvas';
import {
  imageToolStylesMap,
  MAX_GENERATOR_SIZE,
  MIN_GENERATOR_SIZE,
  upscaleStylesList,
} from '@shakkerai/pro-constants';
import { getElementGenInfo } from '@shakkerai/pro-hooks';

export const startInpainting = (elementId, mode = 'inpaint') => {
  // const element = elements.find((i) => i.id === elementId);
  // const fromGallery = element.from === 'gallery';

  if (mode === 'inpaint') {
    getCanvasState().startInpainting(elementId);
    getTempState().updateTempState({ inpaintingId: elementId });
  } else if (mode === 'changeFace') {
    getTempState().updateTempState({ changeFaceId: elementId });
  }

  fetchImageSegmentShapes(elementId, mode);
  getViewport().locateElement(elementId, { fitView: true, animate: true });

  // const [segments, lassos, rectangles] = inpaint.selections.reduce(
  //   (acc, cur) => {
  //     if (cur.sourceType === 'segment') {
  //       acc[0] += 1;
  //     } else if (cur.sourceType === 'lasso') {
  //       acc[1] += 1;
  //     } else if (cur.sourceType === 'rect') {
  //       acc[2] += 1;
  //     }
  //     return acc;
  //   },
  //   [0, 0, 0],
  // );
  // eslint-disable-next-line react-hooks/exhaustive-deps
};

export const cancelInpainting = (elementId, options) => {
  getCanvasState().cancelInpainting(elementId, options);
  getTempState().updateTempState({ inpaintingId: null });
};

export const applyOutpaintSize = (elementId, { width, height }) => {
  const { elements, updateElement } = getCanvasState();
  const image = elements.find((element) => element.id === elementId);

  if (!image || !image.outpaint) {
    return;
  }

  const minWidth = Math.max(MIN_GENERATOR_SIZE, Math.round(image.width * image.scaleX));
  const minHeight = Math.max(MIN_GENERATOR_SIZE, Math.round(image.height * image.scaleY));

  const nextRect = { ...image.outpaintRect };

  if (typeof width !== 'undefined') {
    width = Math.max(minWidth, Math.min(MAX_GENERATOR_SIZE, width - (width % 8)));

    nextRect.scaleX = width / (nextRect.width || minWidth);
    nextRect.x = (image.width * image.scaleX - width) / 2;
  }

  if (typeof height !== 'undefined') {
    height = Math.max(minHeight, Math.min(MAX_GENERATOR_SIZE, height - (height % 8)));
    nextRect.scaleY = height / (nextRect.height || minHeight);
    nextRect.y = (image.height * image.scaleY - height) / 2;
  }

  const { width: bgW, height: bgH, scaleX, scaleY } = nextRect;
  const { width: imgWidth, height: imgHeight } = image;
  const bgWidth = bgW * scaleX;
  const bgHeight = bgH * scaleY;

  updateElement(elementId, {
    outpaintRect: {
      ...nextRect,
      relativeXY: {
        left: Math.round((bgWidth - imgWidth) / 2),
        right: Math.round((bgWidth - imgWidth) / 2),
        top: Math.round((bgHeight - imgHeight) / 2),
        bottom: Math.round((bgHeight - imgHeight) / 2),
      },
    },
  });
};

export const tryFixOutpaintParams = async (elementId) => {
  const { elements, generators, taskMap, updateCanvasData, updateElement } = getCanvasState();
  const image = elements.find((element) => element.id === elementId);

  const outpaintGenerator = generators.find((item) => item.id === image.outpaint);

  if (image && image.outpaint && image.outpaintRect && outpaintGenerator) {
    const outpaintRect = image.outpaintRect;

    // 检查此时的图片尺寸还在不在outpaintRect的范围内
    const rightGap = outpaintRect.width * outpaintRect.scaleX + outpaintRect.x - image.width * image.scaleX;
    const bottomGap = outpaintRect.height * outpaintRect.scaleY + outpaintRect.y - image.height * image.scaleY;

    let needFix = false;
    const fixedData = { ...outpaintRect };

    if (rightGap < 0) {
      needFix = true;
      fixedData.x = outpaintRect.x - rightGap;

      if (fixedData.x > 0) {
        fixedData.width = image.width;
        fixedData.scaleX = image.scaleX;
        fixedData.x = 0;
      }
    }

    if (bottomGap < 0) {
      needFix = true;
      fixedData.y = outpaintRect.y - bottomGap;

      if (fixedData.y > 0) {
        fixedData.height = image.height;
        fixedData.scaleY = image.scaleY;
        fixedData.y = 0;
      }
    }

    if (needFix) {
      updateElement(elementId, {
        outpaintRect: fixedData,
      });
    }

    return;
  }

  if (!outpaintGenerator) {
    const genInfo = await getElementGenInfo(image);
    const outpaintGenerator = createOutpaintGenerator(image, genInfo);
    const x = image.width * image.scaleX;
    const y = image.height * image.scaleY;
    const maxSide = Math.max(x, y);
    updateCanvasData(
      {
        elements: elements.map((element) => {
          if (element.id !== image.id) return element;
          return Object.assign({}, element, {
            outpaint: outpaintGenerator.id,
            outpaintRect: {
              x: 0,
              y: 0,
              scaleX: image.scaleX,
              scaleY: image.scaleY,
              width: maxSide,
              height: maxSide,
              relativeXY: {
                left: 0,
                top: 0,
                right: maxSide > x ? maxSide - x : 0,
                bottom: maxSide > y ? maxSide - y : 0,
              },
            },
          });
        }),
        generators: generators.concat([outpaintGenerator]),
        taskMap: Object.assign(
          {
            [outpaintGenerator.id]: clone(initialTaskData),
          },
          taskMap,
        ),
      },
      {
        skipTracking: true,
      },
    );
  }
};

export const initImageSubGenerators = async (image, genInfo = null, onStyleReady) => {
  if (!image || image.type !== 'image') return;

  let originalPrompt = genInfo?.extendedPrompt || genInfo?.prompt || '';

  const { elements, generators, taskMap, updateCanvasData } = getCanvasState();
  const { width, height, scale } = getGenerateSizeFromElement(image);
  const outpaintGenerator = createOutpaintGenerator(image, genInfo);
  const inpaintGenerator = createInpaintGenerator(image, width, height, genInfo);
  const changefaceGenerator = createChangefaceGenerator(image, width, height, genInfo);
  const upscaleGenerator = createUpscaleGenerator(image, genInfo);
  const isUploadedImage = !image.innerImageId;

  if (originalPrompt) {
    tryTranslatePrompt(originalPrompt).then((prompt) => {
      updateGenerateParams(outpaintGenerator.id, { prompt }, { skipTracking: true });
    });
  }

  console.log(`[DEBUG] outpaintGenerator`, outpaintGenerator);

  fetchImageStyle(image).then((res) => {
    if (!res) return;
    let { description: prompt, substyle } = res;

    if (!substyle) substyle = imageToolStylesMap.Unknown;

    // 获取到style之后，更新一下每个不同生成器的风格类型
    const imageToolStyles = imageToolStylesMap[substyle] || imageToolStylesMap.Unknown;
    const generators = getCanvasState('generators');

    console.log(`[DEBUG] res`, res);
    onStyleReady?.(res);

    // 自动选择匹配到的风格
    updateCanvasData({
      generators: generators.map((generator) => {
        if (generator.id === inpaintGenerator.id) {
          if (isUploadedImage) {
            return Object.assign({}, generator, {
              params: Object.assign({}, generator.params, { cfgScale: 3.5, modelPreset: 'custom' }),
            });
          } else {
            return generator;
          }
        } else if (generator.id === outpaintGenerator.id) {
          const updatedParams = {
            cfgScale: 7,
            modelPreset: imageToolStyles.outpaint,
          };

          if (!originalPrompt && prompt) {
            updatedParams.prompt = prompt;
          }

          return Object.assign({}, generator, {
            params: Object.assign({}, generator.params, updatedParams),
          });
        } else if (generator.id === upscaleGenerator.id) {
          const matchedUpscaleStyle =
            upscaleStylesList.find((item) => item.value === imageToolStyles.upscale) || upscaleStylesList[0];

          return Object.assign({}, generator, {
            params: Object.assign({}, generator.params, {
              prompt: prompt,
              upscale_Style_info: matchedUpscaleStyle,
              upscale_strength: matchedUpscaleStyle.upscale_strength,
              upscale_lora_weight: matchedUpscaleStyle.upscale_lora_weight,
              upscale_cfgScale: matchedUpscaleStyle.upscale_cfgScale,
              upscale_controlWeight: matchedUpscaleStyle.upscale_controlWeight,
            }),
          });
        } else if (generator.id === changefaceGenerator.id) {
          return Object.assign({}, generator, {
            params: Object.assign({}, generator.params, { prompt, modelPreset: imageToolStyles.changeFace }),
          });
        }

        return generator;
      }),
    });
  });

  const currentImageTool = getTempState('topImagesTool');

  const inpaint = {
    enabled: currentImageTool === 'inpaint',
    extend: 2,
    brushSize: 100,
    eraserSize: 100,
    invert: false,
    selections: [],
    status: currentImageTool === 'inpaint' ? 'selecting' : 'idle',
    currentSelectionType: 'segment',
    generatorId: inpaintGenerator.id,
  };

  const updateData = {
    inpaint,
    outpaint: outpaintGenerator.id,
    upscaler: upscaleGenerator.id,
    changeFace: changefaceGenerator.id,
  };

  if (scale < 1) {
    if (!genInfo || genInfo?.generateType !== 'upscale') {
      // 如果图片超出了最大尺寸，就需要限制到最大尺寸
      updateData.scaleX = image.scaleX * scale;
      updateData.scaleY = image.scaleY * scale;
    }
  }

  updateCanvasData(
    {
      elements: elements.map((element) => {
        if (element.id !== image.id) return element;
        return Object.assign({}, element, updateData);
      }),
      generators: generators.concat([inpaintGenerator, outpaintGenerator, upscaleGenerator, changefaceGenerator]),
      taskMap: Object.assign(
        {
          [inpaintGenerator.id]: clone(initialTaskData),
          [changefaceGenerator.id]: clone(initialTaskData),
          [outpaintGenerator.id]: clone(initialTaskData),
          [upscaleGenerator.id]: clone(initialTaskData),
        },
        taskMap,
      ),
    },
    {
      skipTracking: true,
    },
  );
};

export const initExternalImageSubGenerators = async (elementId) => {
  const elements = getCanvasState('elements');
  const element = elements.find((element) => element.id === elementId);

  if (!element) return;

  const { startLoading, stopLoading } = getLoadings();
  startLoading(`preparing-${elementId}`);

  try {
    initImageSubGenerators(element, null, () => {
      stopLoading(`preparing-${elementId}`);
    });
  } catch (error) {
    console.log(error);
    stopLoading(`preparing-${elementId}`);
  }
};

export const updateRemixAndTransferSize = (elementId, size) => {
  const { elements, updateCanvasData } = getCanvasState();
  const image = elements.find((element) => element.id === elementId);

  if (!size || !image || image.type !== 'image') return;
  if (typeof size.width !== 'number' || typeof size.height !== 'number') return;

  const nextScaleX = size.width / image.width;
  const nextScaleY = size.height / image.height;

  updateCanvasData({
    elements: elements.map((element) => {
      if (element.id !== elementId) return element;
      return Object.assign({}, element, { scaleX: nextScaleX, scaleY: nextScaleY });
    }),
  });
};
